import type { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import type { TranslationKey } from "@privacy/translations";
import { Translate, useTranslation } from "@privacy/translations";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { GetPartnerByIdResponse, RoleConnectedEntitiesFormType } from "../../api/partners";
import { archivePartnerMutationOptions, partnerByIdQueryOptions } from "../../api/partners";
import { ReplaceAndArchiveDialog as ReplaceAndArchiveShell } from "../../shared/ReplaceAndArchiveDialog";
import { ReplacePartnerDialog } from "./ReplacePartnerDialog";
import { ROLES_INFO } from "./constants";
import { CONNECTED_ENTITIES_FORM_TYPES_INFO } from "./PartnerRoleSubDrawerConnectedEntities";
import { DynamicShared } from "../../dynamicShared";
import { Drawer } from "../../shared/Drawer";
import { InfoIcon, TriangleAlertIcon } from "../../shared/icons";
import { useHoverPopover } from "../../shared/HoverPopover";
import { MissingPermissionInfoPopover } from "../../shared/MissingPermissionInfoPopover";
import { trackEvent } from "../../shared/Snowplow";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";

type PartnerConnectionRow = {
  id: string;
  responseId: string;
  name: string;
  formType: RoleConnectedEntitiesFormType;
  role: GetPartnerByIdResponse["rolesAndRelationships"][number]["role"];
};

const FORM_TYPE_LABELS: Record<RoleConnectedEntitiesFormType, TranslationKey> = {
  privacyRegistration: "Partner.Role.AssociatedFormTypeLabel.PrivacyRegistration",
  privacyProcessorRegistration: "Partner.Role.AssociatedFormTypeLabel.PrivacyProcessorRegistration",
  system: "Partner.Role.AssociatedFormTypeLabel.System",
};

const buildColumns = (t: (key: TranslationKey) => string): GridColDef<PartnerConnectionRow>[] => [
  {
    field: "name",
    width: 300,
    headerName: t("Partner.ReplaceAndArchiveDialog.NameHeader"),
    renderCell: ({ row }) => {
      const { Icon2 } = CONNECTED_ENTITIES_FORM_TYPES_INFO[row.formType];
      return (
        <Drawer.GridCell>
          <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
            <Icon2 size="sm" />
            <Typography
              sx={{
                fontWeight: "600",
                lineHeight: 1.2,
                overflowWrap: "anywhere",
                hyphens: "auto",
                fontSize: theme => theme.typography.pxToRem(14),
              }}
            >
              {row.name}
            </Typography>
          </Stack>
        </Drawer.GridCell>
      );
    },
  },
  {
    field: "formType",
    width: 150,
    headerName: t("Partner.ReplaceAndArchiveDialog.FormTypeHeader"),
    renderCell: ({ row }) => (
      <Drawer.GridCell>
        <DynamicShared.Chip variant="choice" label={t(FORM_TYPE_LABELS[row.formType])} />
      </Drawer.GridCell>
    ),
  },
  {
    field: "role",
    width: 300,
    headerName: t("Partner.ReplaceAndArchiveDialog.RoleHeader"),
    renderCell: ({ row }) => (
      <Drawer.GridCell>
        <DynamicShared.Chip variant="choice" label={t(ROLES_INFO[row.role].labelKey)} />
      </Drawer.GridCell>
    ),
  },
];

export const ReplaceAndArchiveDialog = ({ partnerId, onClose }: { partnerId: string; onClose: () => void }) => {
  const { t } = useTranslation();
  const currentOrganisation = useCurrentOrganisation();
  const archivePartnerMutation = useMutation(archivePartnerMutationOptions(partnerId));
  const partnerQuery = useQuery(partnerByIdQueryOptions(partnerId));
  const partner = partnerQuery.data;

  const accessibleConnectedEntitiesCount =
    partner?.rolesAndRelationships.reduce(
      (acc, roleAndRelationship) => acc + roleAndRelationship.connectedEntities.length,
      0
    ) ?? 0;
  const notAccessibleConnectedEntitiesExists = partner
    ? partner.totalConnectedEntitiesCount > accessibleConnectedEntitiesCount
    : false;

  const rows: PartnerConnectionRow[] =
    partner?.rolesAndRelationships.flatMap(({ connectedEntities, formType, role }) =>
      connectedEntities.map(entity => ({
        id: `${entity.responseId}:${formType}:${role}`,
        responseId: entity.responseId,
        name: entity.responseName,
        formType,
        role,
      }))
    ) ?? [];

  return (
    <ReplaceAndArchiveShell<PartnerConnectionRow>
      onClose={onClose}
      isLoading={!partner}
      title={t("Partner.Archive.Dialog.Title")}
      subtitle={
        partner && partner.totalConnectedEntitiesCount > 0
          ? t("Partner.Archive.Dialog.Subtitle", {
              name: partner.name,
              asterisk: notAccessibleConnectedEntitiesExists ? "*" : "",
              totalConnectedEntitiesCount: partner.totalConnectedEntitiesCount,
              totalConnectedEntitiesLabel: t(
                partner.totalConnectedEntitiesCount === 1
                  ? "Partner.ReplaceAndArchiveDialog.TotalConnectedEntitiesLabel.Singular"
                  : "Partner.ReplaceAndArchiveDialog.TotalConnectedEntitiesLabel.Plural"
              ),
            })
          : undefined
      }
      headerSlot={notAccessibleConnectedEntitiesExists ? <NotAccessibleConnectionsExistsWarning /> : undefined}
      rows={rows}
      getRowId={row => row.id}
      columns={buildColumns(t)}
      searchPlaceholder={t("Drawer.SubDrawer.GridSearchPlaceholder")}
      replaceSelected={{
        label: t("Partner.Grid.Drawer.ReplacePartnerForSelected"),
        renderDialog: ({ selectedRows, onClose: onReplaceClose }) => (
          <ReplacePartnerDialog
            onClose={onReplaceClose}
            partnerId={partnerId}
            partnerName={partner?.name ?? ""}
            connections={selectedRows}
          />
        ),
      }}
      archive={{
        label: t("Partner.Archive.Label"),
        isDisabled: !partner || partner.totalConnectedEntitiesCount > 0,
        isPending: archivePartnerMutation.isPending,
        onArchive: () => {
          if (!partner) return;
          trackEvent.archivePartner(partner.name, currentOrganisation.name);
          archivePartnerMutation.mutate(undefined, { onSuccess: () => onClose() });
        },
      }}
      actionsSlot={
        notAccessibleConnectedEntitiesExists && partner && partner.rolesAndRelationships.length > 0 ? (
          <NotAccessibleConnectedEntitiesInfo
            totalConnectedEntitiesCount={partner.totalConnectedEntitiesCount}
            accessibleConnectedEntitiesCount={accessibleConnectedEntitiesCount}
            formType={partner.rolesAndRelationships[0].formType}
          />
        ) : undefined
      }
      emptyState={
        <Drawer.GridNoRowsOverlay
          labelKey={
            notAccessibleConnectedEntitiesExists
              ? "Partner.ReplaceAndArchiveDialog.EmptyState.NoAccessibleConnectionsButTotalConnectionsExist"
              : "Partner.ReplaceAndArchiveDialog.EmptyState.NoConnections"
          }
        />
      }
    />
  );
};

const NotAccessibleConnectionsExistsWarning = () => {
  return (
    <Box
      sx={[
        {
          mt: 3,
        },
        theme => ({
          display: "flex",
          alignItems: "center",
          width: "fit-content",
          backgroundColor: theme.palette.brandOrange["50"],
          borderRadius: 1,
          border: `1px solid ${theme.palette.brandOrange["200"]}`,
          px: 3,
          py: 2,
          gap: 2,
        }),
      ]}
    >
      <TriangleAlertIcon color={theme => theme.palette.brandOrange["500"]} size={16} />{" "}
      <Typography
        sx={{
          fontSize: theme => theme.typography.pxToRem(14),
          lineHeight: 1.3,
          color: theme => theme.palette.brandOrange["900"],
          textWrap: "pretty",
          maxWidth: "500px",
        }}
      >
        <Translate translationKey="Partner.ReplaceAndArchiveDialog.PermissionWarning" values={{ newLine: <br /> }} />
      </Typography>
    </Box>
  );
};

export const NotAccessibleConnectedEntitiesInfo: FC<{
  totalConnectedEntitiesCount: number;
  accessibleConnectedEntitiesCount: number;
  formType: RoleConnectedEntitiesFormType;
}> = ({ totalConnectedEntitiesCount, accessibleConnectedEntitiesCount, formType }) => {
  const { popoverProps, anchorProps } = useHoverPopover();
  return (
    <Stack sx={{ pr: 1 }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: "center", justifyContent: "end" }}>
        <Typography
          sx={[
            {
              fontSize: theme => theme.typography.pxToRem(12),
            },
            theme => ({ color: theme.palette.text.tertiary }),
          ]}
        >
          <Translate
            translationKey={FORM_TYPE_TO_EMPTY_STATE_INFO_MESSAGE[formType]}
            values={{
              count: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {accessibleConnectedEntitiesCount}
                </Box>
              ),
              totalCount: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {totalConnectedEntitiesCount}
                </Box>
              ),
            }}
          />
        </Typography>
        <MissingPermissionInfoPopover popoverProps={popoverProps} />
        <Box {...anchorProps}>
          <InfoIcon color={theme => theme.palette.primary.main} strokeWidth={2.5} size={10} />
        </Box>
      </Stack>
    </Stack>
  );
};

const FORM_TYPE_TO_EMPTY_STATE_INFO_MESSAGE: Record<RoleConnectedEntitiesFormType, TranslationKey> = {
  privacyRegistration: "Partner.ReplaceAndArchiveDialog.TotalConnections.Info",
  privacyProcessorRegistration: "Partner.ReplaceAndArchiveDialog.TotalConnections.Info",
  system: "Partner.ReplaceAndArchiveDialog.TotalConnections.Info.SystemProvider",
};
